import * as XLSX from 'xlsx';

/* Flat fallback — used when only a raw status is available */
const STATUS_LABEL = {
  present:         'Full Day',
  late:            'Late',
  'short-leave':   'Short Leave',
  'half-day':      'Half Day',
  'missing-punch': 'Missing Punch',
  leave:           'Leave',
  'half-leave':    'Half Day Leave',
  holiday:         'Holiday',
  'no-record':     'No Record',
};

/* Reason-refined labels — used when status + reason are both present */
const STATUS_REASON_LABEL = {
  'short-leave|morning':  'Morning Short Leave',
  'short-leave|evening':  'Evening Short Leave',
  'half-day|morning':     'Morning Half Day',
  'half-day|evening':     'Evening Half Day',
  'half-day|early-out':   'Evening Half Day',
};

function resolveStatusLabel(r) {
  if (r.StatusLabel) return r.StatusLabel;

  const combo = STATUS_REASON_LABEL[`${r.Status}|${r.StatusReason}`];
  if (combo) return combo;

  return STATUS_LABEL[r.Status] || r.Status || '';
}

const pad2 = (n) => String(n).padStart(2, '0');

const stamp = (d = new Date()) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
  `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const HOUR_FMT = '0.00" h";-0.00" h";"—"';

export function exportAttendanceOverviewToExcel({ rows, filters }) {
  const activeFilters =
    Object.entries(filters || {})
      .filter(([, v]) => v && v !== 'All')
      .map(([k, v]) => `${k}: ${v}`)
      .join('   |   ') || 'None';

  const headerRows = [
    ['ATTENDANCE OVERVIEW'],
    [`Filters: ${activeFilters}`],
    [`Generated: ${stamp()}`],
    [`Records: ${rows.length}`],
    [],
  ];

  // ── Columns ──
  const columns = [
    { key: 'ServiceNo', label: 'Service No', width: 12 },
    { key: 'Name', label: 'Name', width: 26 },
    { key: 'Date', label: 'Date', width: 12 },
    { key: 'Day', label: 'Day', width: 10 },
    {
      key: 'Status',
      label: 'Status',
      width: 20,
      format: resolveStatusLabel,
    },
    { key: 'InTime', label: 'In Time', width: 10 },
    { key: 'OutTime', label: 'Out Time', width: 10 },
    { key: 'shiftName', label: 'Shift Type', width: 14 },
    { key: 'TotalHours', label: 'Total Hours', width: 14, hours: true },
    { key: 'NormalHours', label: 'Normal Hours', width: 14, hours: true },
    { key: 'OTHours', label: 'OT Hours', width: 12, hours: true },
    { key: 'OTHoursRate', label: 'OT Hours Rate', width: 14, numeric: true },
    { key: 'AdjustedOTHours', label: 'Adjusted OT Hours', width: 18, hours: true },
    // { key: 'PreOT',          label: 'Pre OT Hours',   width: 13, hours: true },
    // { key: 'PostOT',         label: 'Post OT Hours',  width: 13, hours: true },
    { key: 'Department', label: 'Department', width: 22 },
    { key: 'Location', label: 'Location', width: 22 },
    { key: 'Category', label: 'Category', width: 18 },
  ];

  const columnHeader = columns.map((c) => c.label);

  const bodyRows = rows.map((r) =>
    columns.map((c) => {
      if (c.format) return c.format(r);

      const raw = r[c.key];

      if (c.hours) {
        const n = num(raw);
        return n > 0 ? Number(n.toFixed(2)) : 0;
      }

      if (c.numeric) return num(raw);

      return raw === null || raw === undefined ? '' : raw;
    })
  );

  const totalsRow = columns.map((c) => {
    if (c.key === 'ServiceNo') {
      return `TOTAL — ${rows.length} record${rows.length === 1 ? '' : 's'}`;
    }

    if (c.hours) {
      return Number(
        rows.reduce((s, r) => s + num(r[c.key]), 0).toFixed(2)
      );
    }

    return '';
  });

  const aoa = [...headerRows, columnHeader, ...bodyRows, [], totalsRow];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = columns.map((c) => ({ wch: c.width || 12 }));

  const hourColIdxs = columns
    .map((c, i) => (c.hours ? i : -1))
    .filter((i) => i >= 0);

  const headerRowCount = headerRows.length;
  const dataStart = headerRowCount + 1;
  const dataEnd = dataStart + bodyRows.length - 1;
  const totalsRowIdx = dataEnd + 2;

  for (let r = dataStart; r <= dataEnd; r += 1) {
    hourColIdxs.forEach((c) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) return;
      ws[addr].z = HOUR_FMT;
    });
  }

  hourColIdxs.forEach((c) => {
    const addr = XLSX.utils.encode_cell({ r: totalsRowIdx, c });
    if (!ws[addr]) return;
    ws[addr].z = HOUR_FMT;
  });

  const lastCol = columns.length - 1;
  ws['!merges'] = headerRows
    .slice(0, 4)
    .map((_, i) => ({ s: { r: i, c: 0 }, e: { r: i, c: lastCol } }));

  ws['!freeze'] = { xSplit: 2, ySplit: headerRows.length + 1 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Attendance Overview');

  const d = new Date();
  const filename = `Attendance_Overview_${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}